import React from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { music24PlanCapabilities, type Music24PlanTier } from 'fym-common/src/license/music24Plans';
import { TypedMemo } from '../../../common/types';
import { Panel } from '../../components/primitives/Panel';
import { SMLButton } from '../../components/primitives/SMLButton';
import { planNameMessages } from '../../config/music24PlanNames';
import type { Music24Plan } from '../../config/music24StripePlans';
import { monthlyDollars, plansWithMoreCredits } from '../browseFormat';
import { browseCreditMessages as messages } from '../browseMessages';
/* eslint-disable css-modules/no-unused-class -- a shared stylesheet; each part uses its own slice of it */
import styles from './BrowseRail.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface BrowseUpgradePathProps {
  plan: Music24PlanTier;
  portalUrl: string | null;
  openPlans: () => void;
}

/** One plan card: opens a day, the filters and the follows (design b5). */
const PlanFacts: React.FC<{ tier: Music24PlanTier }> = ({ tier }) => {
  const intl = useIntl();
  const caps = music24PlanCapabilities[tier];
  let filters: string | null = null;
  if (caps.canFilterByMusicService) {
    filters = intl.formatMessage(messages.filterCountryService);
  } else if (caps.canFilterGeographically) {
    filters = intl.formatMessage(messages.filterCountry);
  }
  return (
    <ul className={styles.stepList}>
      <li>{intl.formatMessage(messages.opensADay, { count: caps.dailyBrowseCredits })}</li>
      {filters !== null && <li>{filters}</li>}
      <li>
        {intl.formatMessage(messages.followLimits, {
          artists: caps.artistsFollowLimit,
          tracks: caps.tracksFollowLimit,
        })}
      </li>
      {filters === null && <li>{intl.formatMessage(messages.historyDays, { days: caps.historyDaysLimit })}</li>}
    </ul>
  );
};

/**
 * The upgrade path when the credits are used up (design b5): the current plan, then each plan with
 * more opens a day. The button opens the Stripe portal on a paid plan, else the plan window.
 */
const BrowseUpgradePathComponent: React.FC<BrowseUpgradePathProps> = ({ plan, portalUrl, openPlans }) => {
  const intl = useIntl();
  const current = music24PlanCapabilities[plan].dailyBrowseCredits;
  const next: Music24Plan[] = plansWithMoreCredits(plan);

  return (
    <div className={styles.path}>
      <Panel nested className={clsx(styles.step, styles.stepCurrent)}>
        <div className={styles.stepName}>
          {intl.formatMessage(planNameMessages[plan])}
          <span className={styles.stepTag}>{intl.formatMessage(messages.yourPlan)}</span>
        </div>
        <PlanFacts tier={plan} />
      </Panel>
      {next.map((candidate, index) => {
        const name = intl.formatMessage(planNameMessages[candidate.tier]);
        const times = Math.round(music24PlanCapabilities[candidate.tier].dailyBrowseCredits / Math.max(1, current));
        const label = intl.formatMessage(messages.upgradeTo, { plan: name });
        return (
          <Panel nested tone={index === 0 ? 'brand' : 'default'} key={candidate.id} className={styles.step}>
            <div className={styles.stepName}>
              {name}
              {index === 0 && times > 1 && (
                <span className={styles.stepTag}>{intl.formatMessage(messages.timesMore, { times })}</span>
              )}
            </div>
            <span className={styles.stepPrice}>
              {intl.formatMessage(messages.pricePerMonth, { price: monthlyDollars(candidate) })}
            </span>
            <PlanFacts tier={candidate.tier} />
            {portalUrl !== null ? (
              <SMLButton variant={index === 0 ? 'primary' : 'default'} href={portalUrl}>
                {label}
              </SMLButton>
            ) : (
              <SMLButton variant={index === 0 ? 'primary' : 'default'} onClick={openPlans}>
                {label}
              </SMLButton>
            )}
          </Panel>
        );
      })}
    </div>
  );
};

export const BrowseUpgradePath = TypedMemo(BrowseUpgradePathComponent);
